import { useAuth }       from '@/hooks/useAuth'
import { useAppStore }   from '@/stores/appStore'
import { DownloadMenu }  from '@/components/viewer/DownloadMenu'
import { baseFilename }  from '@/lib/download'

function ForgeMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 52 52" fill="none" aria-hidden="true">
      <polygon points="26,4 48,44 4,44" stroke="var(--ember)" strokeWidth="3" fill="none" />
      <circle cx="26" cy="26" r="4" fill="var(--gold)" />
    </svg>
  )
}

export function TopBar() {
  const { signOut, session }                             = useAuth()
  const { view, selectedRepo, selectedFilePath,
          selectedFileSha, markdownContent,
          backToVault, backToWorkspace }                 = useAppStore()

  const userName  = session?.user?.user_metadata?.['user_name']  as string | undefined
  const avatarUrl = session?.user?.user_metadata?.['avatar_url'] as string | undefined

  return (
    <header className="h-12 bg-forge-obsidian border-b border-forge-border flex items-center px-4 gap-3 flex-shrink-0 z-50">

      {/* Logo */}
      <button
        onClick={backToVault}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
        title="Back to Vault"
      >
        <ForgeMark />
        <span className="text-forge-gold font-display font-bold tracking-[0.2em] text-xs uppercase hidden sm:block">
          ExMarked
        </span>
      </button>

      {/* Separator */}
      <div className="w-px h-5 bg-forge-border flex-shrink-0" />

      {/* Breadcrumb */}
      <nav className="flex-1 flex items-center gap-1.5 text-xs overflow-hidden min-w-0">
        {view === 'vault' ? (
          <span className="text-forge-ash uppercase tracking-wider">Vault</span>
        ) : (
          <>
            <button
              onClick={backToVault}
              className="text-forge-ash hover:text-forge-primary transition-colors uppercase tracking-wider flex-shrink-0"
            >
              Vault
            </button>
            <span className="text-forge-border flex-shrink-0">/</span>
            <button
              onClick={view === 'viewer' ? backToWorkspace : undefined}
              className={[
                'truncate transition-colors',
                view === 'viewer'
                  ? 'text-forge-primary hover:text-forge-gold cursor-pointer'
                  : 'text-forge-gold cursor-default',
              ].join(' ')}
            >
              {selectedRepo ? `${selectedRepo.owner}/${selectedRepo.name}` : '—'}
            </button>
            {view === 'viewer' && selectedFilePath && (
              <>
                <span className="text-forge-border flex-shrink-0">/</span>
                <span className="text-forge-gold truncate" title={selectedFilePath}>
                  {selectedFilePath.split('/').pop()}
                </span>
              </>
            )}
          </>
        )}
      </nav>

      {/* Export — visible only when a document is open */}
      {view === 'viewer' && markdownContent && (
        <DownloadMenu
          content={markdownContent}
          filename={[
            baseFilename(selectedFilePath ?? 'document'),
            selectedRepo?.name,
            selectedFileSha?.slice(0, 7),
          ].filter(Boolean).join(' — ')}
        />
      )}

      {/* Right side */}
      <div className="flex items-center gap-2.5 flex-shrink-0">
        {selectedRepo?.is_private && (
          <span className="hidden sm:flex items-center gap-1 text-[10px] text-forge-ash border border-forge-border rounded px-1.5 py-0.5 uppercase tracking-wider">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" />
            </svg>
            Private
          </span>
        )}

        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={userName ?? 'User'}
            className="w-6 h-6 rounded-full ring-1 ring-forge-border"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-forge-basalt ring-1 ring-forge-border flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-forge-ash" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
            </svg>
          </div>
        )}

        {userName && (
          <span className="text-xs text-forge-ash hidden md:block">{userName}</span>
        )}

        <button
          onClick={signOut}
          className="text-[11px] text-forge-ash hover:text-forge-gold transition-colors uppercase tracking-wider"
        >
          Sign Out
        </button>
      </div>
    </header>
  )
}
